import {
  CheckCircleFilled,
  ClockCircleFilled,
  DisconnectOutlined,
  ExclamationCircleFilled,
  QuestionCircleFilled,
} from "@ant-design/icons";

import type { DataFreshnessState } from "../domain/types";

interface DeriveFreshnessInput {
  updatedAt: string | null;
  now?: Date;
  online?: boolean;
  partial?: boolean;
}

// This pure classifier is intentionally colocated with its only visual consumer.
// eslint-disable-next-line react-refresh/only-export-components
export function deriveFreshness({
  updatedAt,
  now = new Date(),
  online = true,
  partial = false,
}: DeriveFreshnessInput): DataFreshnessState {
  if (!online) return "offline";
  if (partial) return "partial";
  if (!updatedAt) return "stale";
  const ageMinutes = Math.max(0, (now.getTime() - new Date(updatedAt).getTime()) / 60_000);
  if (ageMinutes <= 10) return "live";
  if (ageMinutes <= 90) return "delayed";
  return "stale";
}

const content = {
  live: { text: "实时", iconLabel: "正常", Icon: CheckCircleFilled },
  delayed: { text: "延迟", iconLabel: "提醒", Icon: ClockCircleFilled },
  stale: { text: "陈旧", iconLabel: "警告", Icon: ExclamationCircleFilled },
  partial: { text: "部分", iconLabel: "部分信息", Icon: QuestionCircleFilled },
  offline: { text: "离线", iconLabel: "离线", Icon: DisconnectOutlined },
} satisfies Record<DataFreshnessState, { text: string; iconLabel: string; Icon: typeof CheckCircleFilled }>;

interface DataFreshnessProps {
  state: DataFreshnessState;
  updatedAt?: string | null;
  compact?: boolean;
}

export function DataFreshness({ state, updatedAt = null, compact = false }: DataFreshnessProps) {
  const { text, iconLabel, Icon } = content[state];
  const formatted = updatedAt
    ? new Intl.DateTimeFormat("zh-CN", {
        timeZone: "Asia/Shanghai",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(updatedAt))
    : "暂无更新时间";

  return (
    <span className={`freshness freshness--${state}`} title={`最后接收 ${formatted}`}>
      <span aria-label={iconLabel} role="img"><Icon aria-hidden /></span>
      <span>{text}</span>
      {!compact ? <time dateTime={updatedAt ?? undefined}>更新 {formatted}</time> : null}
    </span>
  );
}
